/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the list column shell (`.lbar`, `.lsub`, `.lbody`) and collapse affordances.
 * @state-owner navigation store
 * @allowed-imports React; workspace store; shared components and hooks; list bodies
 * @forbidden API client; module internals
 * @public-api WorkspaceShell
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { PanelResizer } from '../../shared/components/Resizer';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreIcon,
  SearchIcon,
} from '../../shared/components/VispoIcon';
import { useIsMobile, useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { StudentListBody } from './list/StudentListBody';
import { TeacherListBody } from './list/TeacherListBody';

export interface ListContent {
  readonly title: string;
  readonly subtitle: string;
  readonly body: React.ReactNode;
}

export function ListColumn({ onOpenSearch }: { onOpenSearch(): void }) {
  const t = useTranslator();
  const role = useActiveRole();
  const mobile = useIsMobile();
  const collapsed = useWorkspaceUiStore((state) => state.listCollapsed);
  const toggleList = useWorkspaceUiStore((state) => state.toggleList);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const content = role === 'teacher' ? TeacherListBody() : StudentListBody();

  return (
    <>
      <aside className={`list${!mobile && collapsed ? ' cl' : ''}`} id="list">
        <div className="mtop">
          <div className="ws2">V</div>
          <b id="mtitle">{content.title}</b>
          <div className="av p" id="mav">
            {role === 'teacher' ? '師' : '柏'}
          </div>
        </div>
        <button className="msrch" type="button" onClick={onOpenSearch}>
          <SearchIcon />
          {t('搜尋')}
        </button>
        <div className="msheet">
          <div className="mcards" id="mcards" />
          <div className="lbar">
            <b id="ltitle">{content.title}</b>
            <button
              className="mi"
              title={t('更多')}
              type="button"
              onClick={() => notify(t('更多選項'))}
            >
              <MoreIcon />
            </button>
            <div className="sp" />
            {mobile ? null : (
              <button
                className="cl9"
                title={`${t('收合清單')} (⌘\\)`}
                type="button"
                onClick={toggleList}
              >
                <ChevronLeftIcon />
              </button>
            )}
          </div>
          <div className="lsub" id="lsub" style={{ display: content.subtitle ? 'block' : 'none' }}>
            {content.subtitle}
          </div>
          <div className="lbody" id="lbody">
            {content.body}
          </div>
        </div>
        <PanelResizer max={560} min={260} panel="list" />
      </aside>
      {!mobile && collapsed ? (
        <button
          className="lstub"
          id="lstub"
          title={t('展開清單')}
          type="button"
          onClick={toggleList}
        >
          <ChevronRightIcon />
        </button>
      ) : null}
    </>
  );
}
